import {
  TreadAgentConversation_Read,
  TreadAgentConversation_Read_Listing,
  TreadAgentMessage_Read,
  TreadAgentMessageFile_Read,
  TreadAgentSkill_Read,
  TreadAgentSkill_Read_Nested,
  TreadAgentToolCall_Read,
  TreadAgentVisualization_Read,
  TreadAgentVisualizationSpec,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { TreadAgentToolCallPreview, TreadAgentToolCallState } from '~tread-agent/types';

// A single file attached to a message.
export class TreadAgentMessageFile {
  constructor(
    private _id: string,
    private _filename: string,
    private _content_type: string,
    private _byte_size: number,
    private _url: string,
    private _preview_url: string | null = null,
  ) {}

  public static parse(proto: TreadAgentMessageFile_Read) {
    return new TreadAgentMessageFile(
      proto.id,
      proto.filename,
      proto.content_type,
      proto.byte_size,
      proto.url,
      proto.preview_url ?? null,
    );
  }

  public get id() {
    return this._id;
  }

  public get filename() {
    return this._filename;
  }

  public get contentType() {
    return this._content_type;
  }

  public get byteSize() {
    return this._byte_size;
  }

  public get url() {
    return this._url;
  }

  // A thumbnail representation for images and PDFs; null for files with no visual preview
  // (text, CSV). Drives whether the transcript shows a thumbnail or a file-icon chip.
  public get previewUrl() {
    return this._preview_url;
  }
}

export class TreadAgentMessage {
  constructor(
    private _id: string,
    private _role: TreadAgentMessage_Read['role'],
    // Null for a silent-write turn — a pure tool-call turn that carries a confirmation card
    // (see toolCalls) but no narration text.
    private _content: string | null,
    private _created_at: Dayjs,
    // The write actions (confirmation cards) invoked by this message, oldest first. Read/lookup
    // tool calls are not surfaced by the messages read.
    private _tool_calls: TreadAgentToolCall[],
    private _files: TreadAgentMessageFile[] = [],
    private _visualizations: TreadAgentVisualization[] = [],
  ) {}

  public static parse(proto: TreadAgentMessage_Read) {
    return new TreadAgentMessage(
      proto.id,
      proto.role,
      proto.content ?? null,
      dayjs(proto.created_at),
      proto.tool_calls.map(TreadAgentToolCall.parse),
      proto.files.map(TreadAgentMessageFile.parse),
      proto.visualizations.map(TreadAgentVisualization.parse),
    );
  }

  public get id() {
    return this._id;
  }

  public get role() {
    return this._role;
  }

  public get content() {
    return this._content;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get toolCalls() {
    return this._tool_calls;
  }

  public get visualizations() {
    return this._visualizations;
  }

  public get files() {
    return this._files;
  }
}

export type TreadAgentVisualizationRow = TreadAgentVisualization_Read['rows'][number];

export class TreadAgentVisualization {
  constructor(
    private _id: string,
    private _type: TreadAgentVisualizationSpec['type'],
    private _title: string,
    private _rows: TreadAgentVisualizationRow[],
    private _source_question: string,
    private _series: { key: string; label: string }[],
    private _subtitle: string | null = null,
    private _x: string | null = null,
    private _columns: string[] | null = null,
    private _group_by: string | null = null,
    private _stacked: boolean = false,
  ) {}

  public static parse(proto: TreadAgentVisualization_Read) {
    return new TreadAgentVisualization(
      proto.id,
      proto.spec.type,
      proto.spec.title,
      proto.rows,
      proto.source.question,
      (proto.spec.series ?? []).map((item) => ({
        key: item.key,
        label: item.label ?? item.key,
      })),
      proto.spec.subtitle ?? null,
      proto.spec.x ?? null,
      proto.spec.columns ?? null,
      proto.spec.group_by ?? null,
      proto.spec.stacked ?? false,
    );
  }

  public get id() {
    return this._id;
  }

  public get type() {
    return this._type;
  }

  public get title() {
    return this._title;
  }

  public get rows() {
    return this._rows;
  }

  public get sourceQuestion() {
    return this._source_question;
  }

  public get series() {
    return this._series;
  }

  public get subtitle() {
    return this._subtitle;
  }

  public get x() {
    return this._x;
  }

  public get columns() {
    return this._columns;
  }

  public get groupBy() {
    return this._group_by;
  }

  public get stacked() {
    return this._stacked;
  }
}

class TreadAgentToolCall {
  constructor(
    private _id: string,
    private _name: string,
    private _state: TreadAgentToolCallState,
    private _created_at: Dayjs,
    private _preview: TreadAgentToolCallPreview | null = null,
    private _output: Record<string, unknown> | null = null,
  ) {}

  public static parse(proto: TreadAgentToolCall_Read) {
    return new TreadAgentToolCall(
      proto.id,
      proto.name,
      proto.state,
      dayjs(proto.created_at),
      proto.preview ?? null,
      proto.output ?? null,
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get state() {
    return this._state;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get preview() {
    return this._preview;
  }

  public get output() {
    return this._output;
  }
}

export class TreadAgentConversationListing {
  constructor(
    private _id: string,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _title: string | null = null,
    private _last_activity_at: Dayjs | null = null,
  ) {}

  public static parse(proto: TreadAgentConversation_Read_Listing) {
    return new TreadAgentConversationListing(
      proto.id,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.title ?? null,
      proto.last_activity_at ? dayjs(proto.last_activity_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get title() {
    return this._title;
  }

  public get lastActivityAt() {
    return this._last_activity_at;
  }
}

export class TreadAgentConversation {
  constructor(
    private _id: string,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _pending_tool_calls: TreadAgentToolCall[],
    private _title: string | null = null,
    private _last_activity_at: Dayjs | null = null,
  ) {}

  public static parse(proto: TreadAgentConversation_Read) {
    return new TreadAgentConversation(
      proto.id,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.pending_tool_calls.map(TreadAgentToolCall.parse),
      proto.title ?? null,
      proto.last_activity_at ? dayjs(proto.last_activity_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get pendingToolCalls() {
    return this._pending_tool_calls;
  }

  public get title() {
    return this._title;
  }

  public get lastActivityAt() {
    return this._last_activity_at;
  }
}

export class TreadAgentSkillBasic {
  constructor(
    // Built-in skills ship as files, not records, so they have no id.
    private _id: string | null,
    private _name: string,
    private _description: string,
    private _system: boolean,
  ) {}

  public static parse(proto: TreadAgentSkill_Read_Nested) {
    return new TreadAgentSkillBasic(
      proto.id,
      proto.name,
      proto.description,
      proto.system,
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get description() {
    return this._description;
  }

  public get isSystem() {
    return this._system;
  }
}

export class TreadAgentSkill {
  constructor(
    private _id: string | null,
    private _name: string,
    private _description: string,
    private _content: string,
    private _operations: string[],
  ) {}

  public static parse(proto: TreadAgentSkill_Read) {
    return new TreadAgentSkill(
      proto.id,
      proto.name,
      proto.description,
      proto.content,
      proto.operations,
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get description() {
    return this._description;
  }

  public get content() {
    return this._content;
  }

  public get operations() {
    return this._operations;
  }
}
